// https://learn.microsoft.com/en-us/azure/azure-monitor/app/javascript-framework-extensions?tabs=react
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import {
    ApplicationInsights,
    type ITelemetryItem,
    type Snippet,
} from '@microsoft/applicationinsights-web';

import {
    TelemetryProviderFactory,
    sanitizeOrDrop,
    type RecordDrop,
} from '@myphx-engineering/instrumentation/web';

const AI_CLOUD_ROLE = 'ux-login-prescryptive';
// This instance has no drop reporter wired up; a dropped envelope is simply not sent.
const IGNORE_DROP: RecordDrop = () => {};
const { VITE_APPLICATION_INSIGHTS_CONNECTION_STRING: connectionString } =
    import.meta.env;

export const createApplicationInsights = () => {
    const reactPlugin = new ReactPlugin();

    const snippet: Snippet = {
        config: {
            connectionString,
            correlationHeaderDomains: ['prescryptive.io'],
            enableCorsCorrelation: true,
            enableAutoRouteTracking: true,
            enableRequestHeaderTracking: true,
            extensions: [reactPlugin],
        },
    };
    const ai = new ApplicationInsights(snippet).loadAppInsights();

    ai.addTelemetryInitializer(tagAndSanitize);

    return { ai, reactPlugin };
};

export const telemetryInstance = TelemetryProviderFactory.getInstance({
    buildNumber: import.meta.env.VITE_BUILD_NUMBER,
    instrumentationKey: connectionString,
    cloudRole: AI_CLOUD_ROLE,
});

/** Expose the instance globally so shared components not wired into this
 * app's local telemetry import (e.g. TalkativeInitializer) can still report
 * telemetry. */
window.telemetry = telemetryInstance;

function tagAndSanitize(envelope: ITelemetryItem): boolean {
    if (envelope.tags === undefined) {
        envelope.tags = [];
    }

    envelope.tags['ai.cloud.role'] = AI_CLOUD_ROLE;

    return sanitizeOrDrop(envelope, IGNORE_DROP);
}
